import Child from '../core/Child';
import LinkList from './LinkList';
import ResourceList from './ResourceList';
import Meta from './Meta';
import Resource from './Resource';
import { copyObject, findConnection } from '../libs';
import Connection from './Connection';
import Link from './Link';
import {headerSymbol} from "../http";

class Collection extends Child {
  constructor(args = {}, ) {
    super(args);

    const {
      data = {},
      metaOverride = null,
      connection = null,
      metaOverrideSet,
    } = args;

    this.metaOverride = metaOverride;
    this.metaOverrideSet = metaOverrideSet;
    this.initialMeta = copyObject(data.meta, 'initial collection meta');
    this.metaInstance = new Meta({ data: data.meta, parent: this, metaOverrideSet });
    this.resourceList = new ResourceList({ data: data.data, parent: this, metaOverrideSet });
    this.linkList = new LinkList({
      data: data.links,
      parent: this,
      defaultExport: this,
    });

    if (this.link('self')) {
      // Used to get an empty collection with the same meta
      this.linkList.pushItem('empty', new Link({
        data: {
          name: 'empty',
          url: this.link('self').url({ params: { limit: 0 } }),
        },
        parent: this,
        defaultExport: this,
      }));
    }

    // Apply meta overrides as the final step
    if (args.metaOverride) {
      this.overrideMetaDeprecated(args.metaOverride);
    }
    if (connection) {
      this.connection(connection)
    }
  }

  * [Symbol.iterator]() {
      yield* this.resourceList[Symbol.iterator]();
  }

  copy() {
    return copyObject(this, 'collection');
  }

  link(key) {
    if (key === undefined) {
      throw new Error('Usage: Key is required.');
    }

    const link = this.linkList.item(key);

    if (!link) return null;

    return link;
  }

  log(prefix, logger) {
    for (const link of this.links().list()) {
      logger.log(`${prefix}.link.${link.attr('name')}: `, link.attr('url'));
    }

    for (let i = 0; i < this.resources().length(); i++) {
      const childResource = this.resources().index(i);
      childResource.log(`${prefix}.resources.${i}`, logger);
    }
  }

  meta() {
    return this.metaInstance;
  }

  connection(connectionOrCookie) {
    if (!connectionOrCookie) return this.linkConnection ? this.linkConnection : findConnection(this.parent());

    if (connectionOrCookie instanceof Connection) {
      this.linkConnection = connectionOrCookie;
    } else {
      this.linkConnection = new Connection({ data: { authToken: connectionOrCookie }});
    }

    return this.linkConnection;
  }

  overrideMetaDeprecated(meta) {
    // Update the collection
    this.meta().merge(meta);

    // Update all resources
    this.resources().list().forEach(resource => {
      resource.meta().merge(meta);
    });
  }

  // This re-creates the collection
  overrideMeta(metaOverrideSet) {
    return new Collection({
      data: this.export(),
      metaOverrideSet,
    });
  }

  resources() {
    return this.resourceList;
  }

  links() {
    return this.linkList;
  }

  refresh(args) {
    if (!this.link('self')) {
      throw new Error('No self link on collection');
    }
    return this.link('self').Get(args);
  }

  isValid() {
    return this.link('self') != null;
  }

  create(args = {}) {
    const {
      metaOverrideSet: metaOverrideSetArg,
      useCollectionMetaOverrideSet
    } = args;
    // If a meta override set is provided, use that for the new resource
    // Otherwise if useCollectionMetaOverrideSet is true, then the new resource
    // will be created using the collection's meta override set
    let metaOverrideSet = metaOverrideSetArg;
    if (!metaOverrideSet && useCollectionMetaOverrideSet) {
      metaOverrideSet = this.metaOverrideSet;
    }
    // The meta on the collection object already has the meta override applied to it
    const resource = new Resource({
      data: {
        type: this.meta() && this.meta().type(),
        meta: this.initialMeta, // Pass in the meta that this collection was created with (sans override)
      },
      metaOverrideSet: metaOverrideSet,
      parent: this,
    });

    // This will add the item to the list within the collection and will set its parent to the list
    // rather than the Collection.
    if (args.addToCollection) {

      if (args.addAtIndex != null) {
        this.resources().addItemAtIndex(null, resource, args.addAtIndex)
      } else {
        this.resources().pushItem(null, resource);
      }

      return this;
    } else if (args.addToNewCollection) {
      const newColl = this.copy();
      newColl.resources().removeAllItems();
      newColl.resources().pushItem(null, resource);

      return newColl;
    }

    return resource;
  }

  async fetchAllPages() {
    const getPages = (linkName, col = this) => {
      const pageLink = col.link(linkName);
      if (pageLink === null) {
        this.links().removeItem(linkName);
        return this;
      }

      return pageLink.Get().then((nextCol) => {
        this.resources().merge(nextCol.resources());
        return getPages(linkName, nextCol);
      });
    };

    await Promise.all([getPages('prev'), getPages('next')]);

    // Below we are modifying the link describing this collection to contain all the resources
    const selfURL = new URL(this.link('self').url());
    selfURL.searchParams.set('offset', 0);
    selfURL.searchParams.set('limit', String(this.resources().length()));
    this.links().updateItem(this.link('self').attr('url', selfURL.toString()));

    return this;
  }

  export(args) {
    return {
      links: this.linkList.export(),
      meta: this.initialMeta,
      data: this.resources().list().map(resource => resource.export(args)),
    };
  }

  resetMeta() {
    this.metaInstance = new Meta({ data: this.initialMeta, parent: this });
    return this;
  }

  query(relName, existsFn, doesntExistFn) {
    const query = this.meta().query(relName);

    if (!query) {
      return doesntExistFn();
    }

    const link = new Link({
      data: {
        name: 'self',
        url: query.attr('url'),
      },
      parent: this,
    });

    return existsFn(link, { query });
  }
}

export default Collection;

const responseHeadersSymbol = Symbol('responseHeaders'); // Do not export

class Response extends Collection {
  constructor(args = {}) {
    super(args);

    const {
      data = {},
      metaOverrideSet,
    } = args;

    this._status = data.status;
    this[responseHeadersSymbol] = null;

    if (data[headerSymbol]) {
      this[responseHeadersSymbol] = data[headerSymbol];
      const currConnection = this.connection();
      const cookieHeader = this.getCookieHeader();

      if (currConnection && cookieHeader) {
        currConnection.mergeCookies(this.getCookieHeader())
      } else if (cookieHeader) {
        this.connection(cookieHeader);
      }
    }

    if (this.connection()) {

    }

    // https://jsonapi.org/format/#document-compound-documents
    this._includedResources = new ResourceList({ data: data.included || [], parent: this, metaOverrideSet });
  }

  getHeaders() {
    const headers = this[responseHeadersSymbol];
    if (!headers) {
      return null;
    }

    return headers;
  }

  mustGetHeaders() {
    const headers = this.getHeaders();
    if (headers === null) {
      throw new Error("Headers must be specified");
    }

    return headers;
  }

  getCookieHeader() {
    return this.mustGetHeaders().get('set-cookie');
  }

  getCookies() {
    return this.getCookieHeader().split(';');
  }

  getCookieValue(cookieName) {
    const cookies = this.getCookies();

    for (let i = 0; i < cookies.length; i++) {
      const [name, val] = cookies[i].split('=');
      if (cookieName === name) {
        return val;
      }
    }

    return undefined;
  }

  status() {
    return this._status;
  }

  isNoContent() {
    return this._status === 204;
  }

  included() {
    return this._includedResources;
  }

  log(prefix, logger) {
    super.log(prefix, logger);

    for (let i = 0; i < this.included().length(); i++) {
      const childResource = this.included().index(i);
      childResource.log(`${prefix}.included-resource.${i}`, logger);
    }
  }

  export(args) {
    const collExport = super.export(args)
    const included = this.included().list().map(v => v.export());

    return {
      ...collExport,
      included,
      status: this.status(),
    };
  }
}

export { Response };